// Beta K19 (#1317) — client for the after-booking payment message:
//   GET /api/v2/admin/tenant/service-payment-messages
//   PUT /api/v2/admin/tenant/service-types/:id/payment-message
//
// The READ is the vocabulary client's shape on purpose: a RAW fetch, because
// the frontends deploy on merge and the server later. Until then the deny-by-
// default guard answers 403 `route_not_mapped`, which `request<T>` would turn
// into a generic error and a Retry that can never succeed. That answer is
// `not_deployed` here, and the card renders nothing. The 200 body is
// zod-parsed, so a wrong shape is an error result, never a throw.
import { z } from 'zod';
import { api, readSession, type ApiResponse } from './api.js';

const BASE = '/api/v2/admin/tenant';

const lastChangeSchema = z.object({ at: z.string(), byName: z.string() }).nullable();

const entrySchema = z.object({
  serviceTypeId: z.string(),
  message: z.string().nullable(),
  lastChange: lastChangeSchema,
});
export type ServicePaymentMessageEntry = z.infer<typeof entrySchema>;

const viewSchema = z.object({
  services: z.array(entrySchema),
  limits: z.object({ maxLength: z.number().int().positive() }),
  onlinePayments: z.enum(['off', 'live', 'simulation']).nullable(),
});
export type ServicePaymentMessagesView = z.infer<typeof viewSchema>;

export type ServicePaymentMessagesFetch =
  | { kind: 'ok'; view: ServicePaymentMessagesView }
  /** 403 route_not_mapped: this server predates the route (deploy window). */
  | { kind: 'not_deployed' }
  /** Network, 5xx, 401/403/404, malformed body. */
  | { kind: 'error' };

function isRouteNotMapped(status: number, body: unknown): boolean {
  if (status !== 403 || body === null || typeof body !== 'object') return false;
  const b = body as Record<string, unknown>;
  return b['reason'] === 'route_not_mapped' || b['error'] === 'route_not_mapped';
}

export async function fetchServicePaymentMessages(apiUrl: string): Promise<ServicePaymentMessagesFetch> {
  try {
    const { token } = readSession();
    const res = await fetch(`${apiUrl}${BASE}/service-payment-messages`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    if (res.status === 200) {
      const parsed = viewSchema.safeParse(body);
      return parsed.success ? { kind: 'ok', view: parsed.data } : { kind: 'error' };
    }
    return isRouteNotMapped(res.status, body) ? { kind: 'not_deployed' } : { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
}

export interface ServicePaymentMessageWriteResponse {
  service: ServicePaymentMessageEntry;
  changed: boolean;
  audited: boolean;
}

export const adminServicePaymentMessagesApi = {
  /** `message: null` is the owner's "use the default wording". */
  update: (apiUrl: string, serviceTypeId: string, message: string | null): Promise<ApiResponse<ServicePaymentMessageWriteResponse>> =>
    api.put<ServicePaymentMessageWriteResponse>(
      apiUrl,
      `${BASE}/service-types/${encodeURIComponent(serviceTypeId)}/payment-message`,
      { message },
    ),
};
